"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { animate, stagger, type JSAnimation } from "animejs";
import cn from "clsx";

import s from "./styles.module.css";

type Streak = { x: number; y: number; len: number; w?: boolean; f?: boolean };

// Thin light streaks (x/y in the 0-100 viewBox, len = length downward).
// f = foreground layer (sharp); otherwise background layer (more blur).
// Deterministic placement keeps SSR + client markup identical.
const STREAKS: Streak[] = [
  { x: 6, y: 14, len: 5 },
  { x: 12, y: 40, len: 6, w: true },
  { x: 9, y: 66, len: 5 },
  { x: 16, y: 86, len: 6 },
  { x: 22, y: 24, len: 5, w: true },
  { x: 27, y: 54, len: 6 },
  { x: 24, y: 78, len: 5 },
  { x: 34, y: 16, len: 6 },
  { x: 38, y: 44, len: 5, w: true },
  { x: 33, y: 70, len: 6 },
  { x: 46, y: 30, len: 5 },
  { x: 50, y: 60, len: 6, w: true },
  { x: 44, y: 88, len: 5 },
  { x: 57, y: 20, len: 6 },
  { x: 62, y: 48, len: 5, w: true },
  { x: 58, y: 74, len: 6 },
  { x: 70, y: 34, len: 5 },
  { x: 74, y: 64, len: 6, w: true },
  { x: 68, y: 90, len: 5 },
  { x: 82, y: 26, len: 6 },
  { x: 87, y: 56, len: 5, w: true },
  { x: 80, y: 80, len: 6 },
  { x: 92, y: 42, len: 5 },
  { x: 96, y: 70, len: 6, w: true },
  // foreground (sharp) streaks
  { x: 10, y: 28, len: 4, f: true },
  { x: 19, y: 58, len: 5, f: true, w: true },
  { x: 30, y: 36, len: 4, f: true },
  { x: 41, y: 66, len: 5, f: true },
  { x: 52, y: 32, len: 4, f: true, w: true },
  { x: 64, y: 60, len: 5, f: true },
  { x: 76, y: 40, len: 4, f: true },
  { x: 85, y: 70, len: 5, f: true, w: true },
  { x: 90, y: 18, len: 4, f: true },
  { x: 14, y: 76, len: 5, f: true },
];

const BACK = STREAKS.filter((p) => !p.f);
const FRONT = STREAKS.filter((p) => p.f);

const renderStreaks = (list: Streak[]) =>
  list.map((p, i) => (
    <line
      key={i}
      x1={p.x}
      y1={p.y}
      x2={p.x}
      y2={p.y + p.len}
      vectorEffect="non-scaling-stroke"
      className={cn(s.streak, p.w && s.white)}
    />
  ));

// Soft light streaks drifting up/down in a continuous loop (anime.js, ping-pong).
// Two parallax layers: blurred background + sharp foreground. Accent-tinted;
// respects prefers-reduced-motion.
export default function ChainDust({ className }: { className?: string }) {
  const root = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
      if (!root.current) return;

      const back = root.current.querySelectorAll('[data-layer="back"] line');
      const front = root.current.querySelectorAll('[data-layer="front"] line');

      // useGSAP only cleans up GSAP animations, so anime.js instances must be
      // cancelled by hand on unmount — these loop forever otherwise.
      const animations: JSAnimation[] = [];

      // background: bigger, slower drift
      if (back.length) {
        animations.push(
          animate(back, {
            translateY: [16, -16],
            opacity: [0.22, 0.6],
            duration: () => 5200 + Math.random() * 3600,
            delay: stagger(140, { from: "center" }),
            loop: true,
            alternate: true,
            ease: "inOutSine",
          })
        );
      }

      // foreground: tighter, quicker drift
      if (front.length) {
        animations.push(
          animate(front, {
            translateY: [10, -10],
            opacity: [0.4, 0.9],
            duration: () => 3200 + Math.random() * 2800,
            delay: stagger(120, { from: "center" }),
            loop: true,
            alternate: true,
            ease: "inOutSine",
          })
        );
      }

      return () => animations.forEach((a) => a.cancel());
    },
    { scope: root }
  );

  return (
    <div ref={root} className={cn(s.dust, className)} aria-hidden="true">
      <svg
        className={cn(s.motes, s.layerBack)}
        data-layer="back"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        focusable="false"
      >
        {renderStreaks(BACK)}
      </svg>
      <svg
        className={cn(s.motes, s.layerFront)}
        data-layer="front"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        focusable="false"
      >
        {renderStreaks(FRONT)}
      </svg>
    </div>
  );
}
